import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import {
    Users, ChevronLeft, Search, Trash2,
    Shield, User, Store, AlertCircle, X, Ban, CheckCircle
} from "lucide-react";

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/api/admin/users`;
            if (roleFilter) {
                url += `?role=${roleFilter}`;
            }

            const response = await fetch(url, {
                headers: { Authorization: token }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error("คุณไม่มีสิทธิ์เข้าถึงหน้านี้");
                }
                throw new Error("Failed to fetch users");
            }

            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [roleFilter]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleUpdateRole = async (userId, newRole) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token
                },
                body: JSON.stringify({ role: newRole })
            });

            if (response.ok) {
                fetchUsers();
                setSelectedUser(null);
                alert("อัปเดต role สำเร็จ");
            } else {
                const data = await response.json();
                alert(data.error || "เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("คุณแน่ใจหรือไม่ที่จะลบผู้ใช้นี้?")) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
                method: "DELETE",
                headers: { Authorization: token }
            });

            if (response.ok) {
                fetchUsers();
                setSelectedUser(null);
                alert("ลบผู้ใช้สำเร็จ");
            } else {
                const data = await response.json();
                alert(data.error || "เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const handleBanUser = async (userId) => {
        if (!window.confirm("คุณแน่ใจหรือไม่ที่จะระงับบัญชีผู้ใช้นี้?")) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/ban`, {
                method: "PUT",
                headers: { Authorization: token }
            });

            if (response.ok) {
                fetchUsers();
                setSelectedUser(null);
                alert("ระงับบัญชีผู้ใช้สำเร็จ");
            } else {
                const data = await response.json();
                alert(data.error || "เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnbanUser = async (userId) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/unban`, {
                method: "PUT",
                headers: { Authorization: token }
            });

            if (response.ok) {
                fetchUsers();
                setSelectedUser(null);
                alert("ปลดการระงับบัญชีสำเร็จ");
            } else {
                alert("เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const getRoleBadge = (role) => {
        const config = {
            ADMIN: { bg: "bg-red-100", text: "text-red-700", icon: Shield },
            SELLER: { bg: "bg-green-100", text: "text-green-700", icon: Store },
            USER: { bg: "bg-gray-100", text: "text-gray-700", icon: User }
        };
        const c = config[role] || config.USER;
        const Icon = c.icon;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                <Icon size={12} />
                {role}
            </span>
        );
    };

    const filteredUsers = users.filter(user =>
        user.accountName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center bg-white p-8 rounded-xl shadow-sm">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">{error}</p>
                    <Link to="/admin" className="text-red-600 hover:underline">กลับหน้า Admin</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                            <ChevronLeft className="w-6 h-6" />
                        </Link>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-500 rounded-lg">
                                <Users className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">จัดการผู้ใช้งาน</h1>
                                <p className="text-sm text-gray-500">ทั้งหมด {users.length} คน</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="ค้นหาชื่อหรืออีเมล..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                            />
                        </div>
                        <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        >
                            <option value="">ทุก Role</option>
                            <option value="USER">USER</option>
                            <option value="SELLER">SELLER</option>
                            <option value="ADMIN">ADMIN</option>
                        </select>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อบัญชี</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">อีเมล</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะบัญชี</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        ไม่พบผู้ใช้งาน
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.userId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-500">#{user.userId}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.accountName}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                                        <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                                        <td className="px-6 py-4">
                                            {user.status === "BANNED" ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                    <Ban size={12} />
                                                    ถูกระงับ
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                    <CheckCircle size={12} />
                                                    ปกติ
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => setSelectedUser(user)}
                                                className="text-red-600 hover:text-red-700 font-medium text-sm"
                                            >
                                                จัดการ
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* User Detail Modal */}
                {selectedUser && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4">
                            <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedUser(null)} />
                            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">จัดการผู้ใช้</h3>
                                    <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="space-y-4 mb-6">
                                    <div>
                                        <p className="text-sm text-gray-500">ชื่อบัญชี</p>
                                        <p className="font-medium">{selectedUser.accountName}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">อีเมล</p>
                                        <p className="font-medium">{selectedUser.email}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Role ปัจจุบัน</p>
                                        {getRoleBadge(selectedUser.role)}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <p className="text-sm font-medium text-gray-700">เปลี่ยน Role:</p>
                                    <div className="flex gap-2">
                                        {["USER", "SELLER", "ADMIN"].map(role => (
                                            <button
                                                key={role}
                                                onClick={() => handleUpdateRole(selectedUser.userId, role)}
                                                disabled={actionLoading || selectedUser.role === role}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedUser.role === role
                                                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                                    }`}
                                            >
                                                {role}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {selectedUser.role !== "ADMIN" && (
                                    <>
                                        {selectedUser.status === "BANNED" ? (
                                            <button
                                                onClick={() => handleUnbanUser(selectedUser.userId)}
                                                disabled={actionLoading}
                                                className="w-full mt-6 py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <CheckCircle size={18} />
                                                ปลดการระงับบัญชี
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleBanUser(selectedUser.userId)}
                                                disabled={actionLoading}
                                                className="w-full mt-6 py-3 px-4 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Ban size={18} />
                                                ระงับบัญชี
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDeleteUser(selectedUser.userId)}
                                            disabled={actionLoading}
                                            className="w-full mt-3 py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Trash2 size={18} />
                                            ลบผู้ใช้
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUserManagement;
